import React, {Component} from 'react';
import axios from "axios";
import FullCalendar from '@fullcalendar/react' // must go before plugins
import momentPlugin from '@fullcalendar/moment'
import dayGridPlugin from '@fullcalendar/daygrid' // a plugin!
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import getDateQry from "../../helpers/dateHelpers";
import {Three_Weeks} from "../../helpers/timePeriods";
import _ from 'lodash';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import Moment from 'moment';
import { Formik, Form, Field, ErrorMessage, FormikConsumer } from 'formik';
import ErrorLabel from '../../components/ErrorLabel/ErrorLabel';
import {connect} from "react-redux";
import css from './SurveyCalendar.module.scss';

/**
 * @class
 */
 const SurveyCalendar = class extends Component {
    // ---------- class fields: ----------
    opportunityId = this.props.oppId;
    resurvey = this.props.resurvey ?? false; // is set to true if the calendar is opened from reschedule survey button on the work order page.
    timeSlots = [
        {start: Moment("08:00", "HH:mm"), end: Moment("10:00", "HH:mm")},
        {start: Moment("10:00", "HH:mm"), end: Moment("12:00", "HH:mm")},
        {start: Moment("12:00", "HH:mm"), end: Moment("14:00", "HH:mm")},
        {start: Moment("14:00", "HH:mm"), end: Moment("16:00", "HH:mm")},
        {start: Moment("16:00", "HH:mm"), end: Moment("18:00", "HH:mm")}
    ];

    source = axios.CancelToken.source();

    postRequestFindJobOverview = {
        "limit": 1,
        "skip": 0,
        "fields": [
            "Id",
            "Name",
            "Opportunity__r.Location__c",
            "Opportunity__r.Location__r.Name",
            "Opportunity__r.Any_Code_Gates_Animals__c",
            "Opportunity__r.Name",
            "Opportunity__r.Wifi_Network__c" // pulling wifi network to see if the wifi information is already set on opportunity
        ],
        "where": {
            "Opportunity__c": this.opportunityId,
            "Stage__c": "Survey"
        }
    }

    postRequestFieldTeams = {
        "limit": 500,
        "skip": 0,
        "fields": [
            "Id",
            "Name",
            "Location__c",
            "Workday_Start_Time__c",
            "Workday_End_Time__c",
            "Launch_Date__c",
            "Dismantle_Date__c",
            "Start_Hold__c",
            "End_Hold__c",
            "Tech_1__c"
        ],
        "where": {
            "Launch_Date__c": {$ne: null},
            "Department__c": "Surveyor"
        }
    }

    postRequestFindWorkOrders = (fieldTeamIds) => ({
        "limit": 1000,
        "skip": 0,
        "fields": [
            "Id",
            "Name",
            "Date__c",
            "Start_Time__c",
            "End_Time__c",
            "Duration__c",
            "Assigned_Field_Team__c",
            "Assigned_Field_Team_2__c",
            "Opportunity__r.City__c",
        ],
        "where": {
            "$or": [
                {"Assigned_Field_Team__c": { "$in": fieldTeamIds },},
                {"Assigned_Field_Team_2__c": { "$in": fieldTeamIds }},
            ],
            "Cancel_Date_Time__c": null
        },
        date: getDateQry(Three_Weeks, 'Date__c')
    })

    postRequestFindTimeOff = (fieldTeamIds, techIds) => ({
        "limit": 1000,
        "skip": 0,
        "fields": [
            "Id",
            "Name",
            "Time_Off_Date__c",
            "Field_Team__c",
            "Employee__c",
            "Start_Time__c",
            "End_Time__c",
            "Time_Period__c"
        ],
        "where": {
            "$or": [
                {"Field_Team__c": { "$in": fieldTeamIds }},
                {"Employee__c": { "$in": techIds }}
            ],
            "Cancelled__c": null
        },
        date: getDateQry(Three_Weeks, 'Time_Off_Date__c')
    })

    // ---------- state: ----------
    state = {
        // Fields for related opportunity
        opp: {},
        opportunityName: '',
        jobOverviewId: '',
        FieldTeamId: '',

        // Fields for session
        dateCapacities: [],
        sessionTime: '',
        dateCapacityIds: [],
        locationId: '',
        timeWindow: '',
        startTime: '',
        codeGateAnimals: '',

        calendarTimeSlots: [],

        // Fields for Salesforce Locations data
        locations: [],
        locationName: '',

        isSchedulingSurvey: false,
        isModelOpen: false,
        isMounted: false,
        disableButton: false,
        rssReasons: [],
        uploading: false,
        uploadErrorMessage: '',
        wifiPasswordVisible: false
    };

    // ---------- life cycle methods: ----------
    componentDidMount() {
        window.scrollTo(0, 0);

        const dates = [];
        const startDate = Moment(Three_Weeks.start).startOf("day");
        const endDate = Moment(Three_Weeks.end).startOf("day");

        const i = startDate.clone();
        while(i.isSameOrBefore(endDate, "day")) {
            dates.push(i.format("YYYY-MM-DD"));
            i.add(1, "day");
        }
        
        Promise.all([this.getJobOverview(), this.getFieldTeams()])
        .then(async (res) => {
            const jobOverview = res[0].data.records[0];

            if(!jobOverview){
                return;
            }

            const allFieldTeams = res[1].data.records;
            const opp = jobOverview.Opportunity__r;
            const filteredFieldTeams = allFieldTeams.filter(ft => ft.Location__c === opp.Location__c);
            const calendarTimeSlots = [];

            const fieldTeamIds = filteredFieldTeams.map(ft => ft.Id);
            const techIds = filteredFieldTeams.map(ft => ft.Tech_1__c); // For site survey field teams we are finding time off for just tech 1.
            const [workOrdersRes, timeOffsRes] = await Promise.all([this.getWorkOrders(fieldTeamIds), this.getTimeOff(fieldTeamIds, techIds)]);
            const workOrders = workOrdersRes.data.records;
            const timeOffs = timeOffsRes.data.records;

            const workOrdersMap = []; // [date => [ftId => [wo]]]
            workOrders.forEach(wo => {
                wo.start = Moment(wo.Start_Time__c, "HH:mm");
                wo.end = wo.start.clone().add(wo.Duration__c, "minutes");

                if(!workOrdersMap[wo.Date__c]){
                    workOrdersMap[wo.Date__c] = [];
                }

                // we are not creating a hard copy, so all values set to this map will be reflected on the parent.
                const dateMap = workOrdersMap[wo.Date__c];
                if(!dateMap[wo.Assigned_Field_Team__c]){
                    dateMap[wo.Assigned_Field_Team__c] = [wo];
                } else {
                    dateMap[wo.Assigned_Field_Team__c].push(wo);
                }

                if(wo.Assigned_Field_Team_2__c){
                    if(!dateMap[wo.Assigned_Field_Team_2__c]){
                        dateMap[wo.Assigned_Field_Team_2__c] = [wo];
                    } else {
                        dateMap[wo.Assigned_Field_Team_2__c].push(wo);
                    }
                }
            });

            const timeOffsMap = []; // [date => [ftId => [timeOff]]]
            timeOffs.forEach(timeOff => {
                if(timeOff.Start_Time__c){
                    timeOff.start = Moment(timeOff.Start_Time__c, "HH:mm");
                } else {
                    timeOff.start = Moment('00:01', "HH:mm");
                }
                if(timeOff.End_Time__c){
                    timeOff.end = Moment(timeOff.End_Time__c, "HH:mm");
                } else {
                    timeOff.end = Moment('23:59', "HH:mm");
                }

                if(!timeOffsMap[timeOff.Time_Off_Date__c]){
                    timeOffsMap[timeOff.Time_Off_Date__c] = [];
                }

                // we are not creating a hard copy, so all values set to this map will be reflected on the parent.
                const dateMap = timeOffsMap[timeOff.Time_Off_Date__c];
                if(timeOff.Field_Team__c){
                    if(!dateMap[timeOff.Field_Team__c]){
                        dateMap[timeOff.Field_Team__c] = [timeOff];
                    } else {
                        dateMap[timeOff.Field_Team__c].push(timeOff);
                    }
                }

                if(timeOff.Employee__c){
                    const ft = filteredFieldTeams.find(ft => ft.Tech_1__c === timeOff.Employee__c);

                    if(ft){
                        if(!dateMap[ft.Id]){
                            dateMap[ft.Id] = [timeOff];
                        } else {
                            dateMap[ft.Id].push(timeOff);
                        }
                    }
                }
            });

            filteredFieldTeams.forEach(ft => {
                // giving 1 hour buffer from both start and end of workday.
                const ftWorkdayStart = Moment(ft.Workday_Start_Time__c, "HH:mm").add(1, "hour");
                const ftWorkdayEnd = Moment(ft.Workday_End_Time__c, "HH:mm").subtract(1, "hour");

                dates.forEach(d => {
                    const date = Moment(d, "YYYY-MM-DD");
                    const ftTimeOffs = timeOffsMap[d]?.[ft.Id] ?? [];
                    const ftWorkOrders = workOrdersMap[d]?.[ft.Id] ?? [];

                    if(!ft.Launch_Date__c || Moment(ft.Launch_Date__c, "YYYY-MM-DD").isAfter(date) || // Team isn't launched
                       (ft.Dismantle_Date__c && Moment(ft.Dismantle_Date__c, "YYYY-MM-DD").isSameOrBefore(date)) || // Team is dismantled
                       (ft.Start_Hold__c && Moment(ft.Start_Hold__c, "YYYY-MM-DD").isSameOrBefore(date) && (!ft.End_Hold__c || Moment(ft.End_Hold__c, "YYYY-MM-DD").isSameOrAfter(date)))){ // Team is on hold
                        return;
                    }

                    this.timeSlots.forEach(ts => {
                        // Check if the time slot doesn't fit the field team's workday.
                        if(ts.start < ftWorkdayStart || ts.end > ftWorkdayEnd){
                            return;
                        }

                        // Check if there is a time off for this field team for this day.
                        const overlapTimeOff = ftTimeOffs.find(timeOff => {
                            return ((ts.start >= timeOff.start && ts.start < timeOff.end) || // new starts inside existing
                                    (ts.end > timeOff.start && ts.end <= timeOff.end) ||     // new ends inside existing
                                    (ts.start <= timeOff.start && ts.end >= timeOff.end))     // new completely covers existing;
                        });

                        // If this fieldTeam is off, we are not showing any time slot for it.
                        if(overlapTimeOff){
                            return;
                        }

                        const overlapWorkOrder = ftWorkOrders.find(wo => {
                            return ((ts.start >= wo.start && ts.start < wo.end) || // new starts inside existing
                                    (ts.end > wo.start && ts.end <= wo.end) ||     // new ends inside existing
                                    (ts.start <= wo.start && ts.end >= wo.end))     // new completely covers existing
                        });

                        // If this time slot is booked, show it as busy.
                        if(overlapWorkOrder){
                            calendarTimeSlots.push({title: 'Busy - ' + overlapWorkOrder.Opportunity__r.City__c, start: d + 'T' + ts.start.format("HH:mm"), end:  d +'T' + ts.end.format("HH:mm"), color: '#f5c126'})
                            return;
                        }
                        
                        // If all checks passed, show this time slot as open.
                        calendarTimeSlots.push({title: 'Open', start: d + 'T' + ts.start.format("HH:mm"), end:  d +'T' + ts.end.format("HH:mm"), color: '#63c088'})
                    })  
                })
            })

            this.setState(
                {
                    opp: opp,
                    opportunityName: opp.Name,
                    codeGateAnimals: opp.Any_Code_Gates_Animals__c,
                    jobOverviewId: jobOverview.Id,
                    calendarTimeSlots,
                    locationName:  opp.Location__r.Name,
                    isMounted: true,
                    locationId: opp.Location__c
                });
        });

        axios.get(
            '/sf/WorkOrder/describeField/What_is_the_RSS_reason__c',
            {
                cancelToken: this.source.token
            })
            .then((resPickFields) => {
                this.setState({rssReasons: resPickFields.data.picklistValues});
            })
            .catch(err => console.log('(PUT) Error while trying retrieve RSS values: \n', err));
    };

    componentWillUnmount() {
        this.source.cancel();
    };

    getJobOverview = () => {
        return axios.post(
            '/sf/JobOverview/findNO',
            this.postRequestFindJobOverview,
            {
                cancelToken: this.source.token
            })
    }

    getFieldTeams = () => {
        return axios.post(
            '/sf/fieldTeam/findNO',
            this.postRequestFieldTeams,
            {
                cancelToken: this.source.token
            })
    }

    getWorkOrders = (fieldTeamIds) => {
        return axios.post(
            '/sf/WorkOrder/findNO',
            this.postRequestFindWorkOrders(fieldTeamIds),
            {
                cancelToken: this.source.token
            })
    }

    getTimeOff = (fieldTeamIds, techIds) => {
        return axios.post(
            '/sf/TimeOff/findNO',
            this.postRequestFindTimeOff(fieldTeamIds, techIds),
            {
                cancelToken: this.source.token
            })
    }

    handleEventClick = (info) => {
        let date = Moment(info.event.start).format('M/DD/YYYY');
        let startTime = Moment(info.event.start).format('HH:mm');
        let startEnd = Moment(info.event.start).format('hh:mm A') + ' - ' + Moment(info.event.end).format('hh:mm A');
        let dateFormatTime = Moment(info.event.start);
        let currentTime = Moment();
        let oneDay = Moment().add(1, 'days').set('hour', 23).set('minute', 0);
        let sevenCheck = Moment().set('hour', 21).set('minute', 0);
        
        if(info.event.title === 'Open' && dateFormatTime.get('date') !== currentTime.get('date') && currentTime < dateFormatTime){
            if(Moment(info.event.start).isBetween(currentTime, oneDay)){
                if(currentTime < sevenCheck){
                    this.setState(
                        {
                            isModelOpen: true,
                            sessionTime: date,
                            timeWindow: startEnd.toString(), // just for display purposes.
                            startTime,
                            isSchedulingSurvey: true
                        }
                    );
                }

            } else {
                this.setState(
                    {
                        isModelOpen: true,
                        sessionTime: date,
                        timeWindow: startEnd.toString(),
                        startTime,
                        isSchedulingSurvey: true
                    }
                );
            }
        }
    };

    closeModalOnClick = () => {
        this.setState(
            {
                isSchedulingSurvey: false
            }
        );
    };

    returnToCustomerPage = () => {
        this.props.onShouldShowCalendar(false);
    };

    render() {
        return (
            <React.Fragment>
            <div>
                <h4 className="m-0">Survey Calendar</h4>
                <h1>{this.state.opportunityName}</h1>

                <div className="row">
                    <div className="col-sm-12">
                        <h3>Location</h3>
                    </div>
                </div>
                <div className="row">
                    {
                        this.state.isMounted ? 
                        (
                            <div className="col-sm-12">
                                <h2>{this.state.locationName} Calendar</h2>
                            </div>
                        )
                        :
                        (
                            <div></div>
                        )
                    }
                </div>

                <div className={css.fc}>
                <FullCalendar
                    plugins={[ dayGridPlugin, timeGridPlugin, interactionPlugin, momentPlugin ]}
                    height="auto"
                    initialView="timeGridDay"
                    initialDate={Moment().add(1,'days').format('YYYY-MM-DD')}
                    navLinks="false"
                    hiddenDays={[0]}
                    allDaySlot={false}
                    slotDuration="00:30"
                    slotLabelInterval="01:00"
                    slotMinTime="08:00"
                    slotMaxTime="18:00"
                    slotLabelFormat={
                        {
                            hour: 'numeric',
                            minute: '2-digit',
                            meridiem: 'short'
                        }
                    }
                    titleFormat={ 
                        {
                            year: 'numeric', month: 'short', day: 'numeric'
                        }
                    }
                    headerToolbar={
                        {
                            left: 'prev,next',
                            center: 'title',
                            right: 'timeGridWeek,timeGridDay' 
                        }
                    }
                    footerToolbar={
                        {
                            center: 'today'
                        }
                    }
                    views={
                        {
                            timeGridWeek: {
                                eventClick: false
                            }

                        }
                    }
                    eventTimeFormat={
                        {
                            hour: 'numeric',
                            minute: '2-digit',
                            omitZeroMinute: true,
                            meridiem: 'narrow'
                        }
                    }
                    eventSources={[this.state.calendarTimeSlots]}
                    defaultTimedEventDuration="02:00"
                    slotEventOverlap={false}
                    eventClick={this.handleEventClick}
                />
                </div>

                <div className="col-sm-12 text-right">
                    <button className="btn btn-primary ml-sm-2 mt-3 mt-sm-0 w-100 w-sm-auto" type="button" onClick={this.returnToCustomerPage}>Cancel</button>
                </div>

                <Modal
                    isOpen={this.state.isModelOpen}
                    onRequestClose={() => this.setState({isModelOpen: false})}
                    className="ReactModal"
                    overlayClassName="ReactModal__Overlay"
                    style={{
                        overlay: {
                            backgroundColor: 'rgba(255, 255, 255, 0.75)'
                        },
                        content: {
                            background: 'rgba(53, 53, 53)',
                            maxHeight: 'calc(100vh - 2rem)', // 20vh more than original
                        }
                    }}
                >
                    <Formik 
                        initialValues={{
                            houseStories: '',
                            codesGatesAnimals: this.state.codeGateAnimals,
                            addSurveyNotes: '',
                            customerHome: '',
                            roofAccess: '',
                            backyardAccess: '',
                            surveyType: '',
                            atticRun: '',
                            manuHome: '',
                            existingSolar: '',
                            internet: '',
                            wifiNetwork: '',
                            wifiPassword: '',
                            isThisRss: '',
                            batteryPurpose: '',
                            batteryBackup: '',
                            rssNeeds: [],
                            missingField: ''
                        }}
        
                        validate={
                            (values) => {
                                const errors = {};
                                
                                if (!values.houseStories.trim()) {
                                    errors.houseStories = 'Please Select How Many Stories';
                                    errors.missingField = 'Missing Required Fields';
                                }

                                if (!values.customerHome.trim()) {
                                    errors.customerHome = 'Please Select if the Customer Will Be Home';
                                    errors.missingField = 'Missing Required Fields';
                                }

                                if (!values.roofAccess.trim()) {
                                    errors.roofAccess = 'Please Enter if we have Roof Access';
                                    errors.missingField = 'Missing Required Fields';
                                }

                                if (!values.backyardAccess.trim()) {
                                    errors.backyardAccess = 'Please Enter if we have Backyard Access';
                                    errors.missingField = 'Missing Required Fields';
                                }

                                if (!values.manuHome.trim()) {
                                    errors.manuHome = 'Please Enter if Customer Wants Attic Run';
                                    errors.missingField = 'Missing Required Fields';
                                }

                                if (!values.existingSolar.trim()) {
                                    errors.existingSolar = 'Please Enter if there is Existing Solar';
                                    errors.missingField = 'Missing Required Fields';
                                }

                                if (!values.internet.trim()) {
                                    errors.internet = 'Please Enter if there is Internet Access';
                                    errors.missingField = 'Missing Required Fields';
                                }

                                if (values.surveyType.length === 0) {
                                    errors.surveyType = 'Please Select the Type of Survey Needed';
                                    errors.missingField = 'Missing Required Fields';
                                }

                                // We do not ask these questions if the calendar was openned to reschedule a survey.
                                if(this.resurvey !== true){
                                    if (!values.atticRun.trim()) {
                                        errors.atticRun = 'Please Enter if Customer Wants Attic Run';
                                        errors.missingField = 'Missing Required Fields';
                                    }
    
                                    if (!values.isThisRss.trim()) {
                                        errors.isThisRss = 'Please Enter if this is a Re-Survey';
                                        errors.missingField = 'Missing Required Fields';
                                    }
                                }
        
                                return errors;
                            }
                        }
        
                        onSubmit={
                            // n.b. fields are validated before `onSubmit` fires
                            (values) => {
                                this.setState({
                                    disableButton: true,
                                    uploading: true,
                                    uploadError: false, 
                                    uploadErrorMessage: ''
                                });

                                let groundMountBool = values.surveyType.includes("Ground Mount");
                                let surveyTypeFormatted = values.surveyType?.join(';');
                                let rssNeedsFormatted = values.rssNeeds?.join(';');
                                let surveyDate = Moment(this.state.sessionTime).format('YYYY-MM-DD');
                                let isThisAnRssBool = values.isThisRss === "true" || this.resurvey === true;

                                const oppPayload = {
                                    Id: this.opportunityId,
                                    Ground_Mount__c: groundMountBool,
                                    Any_Code_Gates_Animals__c: values.codesGatesAnimals
                                }

                                if((values.internet === 'Wifi & Cell' || values.internet === 'Wifi Only')){
                                    if(values.wifiNetwork){
                                        oppPayload.Wifi_Network__c = values.wifiNetwork;
                                    }
                                    if(values.wifiPassword){
                                        oppPayload.Wifi_Password__c = values.wifiPassword;
                                    }
                                }

                                axios.put(
                                        '/sf/opportunity/update',
                                        oppPayload,
                                        {
                                            cancelToken: this.source.token
                                        })
                                        .catch((err) => {
                                            console.log('(PUT) Error while trying to update opportunity: \n', err);
                                            this.setState({uploading: false, disableButton: false, uploadError: true, uploadErrorMessage: 'Error Code: ' + JSON.stringify(err)})
                                        });

                                let scheduleSiteSurveyReq = {
                                    TypeOfLead: 'scheduleSiteSurvey',

                                    // /*   Data needed to properly schedule/create new WO. SF side will handle assignment of proper field team. */
                                    LocationId: this.state.locationId,
                                    JobOverviewId: this.state.jobOverviewId,
                                    Date: surveyDate,
                                    StartTime: this.state.startTime,

                                    /*   Fields to be marked set on new WO.   */
                                    How_many_stories_is_the_house_home__c: values.houseStories,
                                    CU_preferences_and_access_info__c: values.codesGatesAnimals,
                                    Will_the_customer_be_home__c: values.customerHome,
                                    Permission_granted_to_access_the_roof__c: values.roofAccess,
                                    Permission_granted_to_access_the_yard__c: values.backyardAccess,
                                    Scheduling_Notes__c: values.addSurveyNotes,
                                    Survey_Type__c: surveyTypeFormatted,
                                    Did_the_customer_request_an_attic_run__c: values.atticRun,
                                    Is_this_a_manufactured_home__c: values.manuHome,
                                    Is_There_Existing_Solar__c: values.existingSolar,
                                    Is_internet_access_available__c: values.internet,
                                    Is_this_an_RSS__c: isThisAnRssBool,
                                    What_is_the_purpose_of_the_battery__c: values.batteryPurpose,
                                    What_does_the_customer_want_backed_up__c: values.batteryBackup,
                                    What_is_the_RSS_reason__c: rssNeedsFormatted
                                };

                                // Adding delay to prevent race condition with the opportunity update.
                                setTimeout(() => {
                                    axios.post('/leadPost', scheduleSiteSurveyReq, {cancelToken: this.source.token})
                                        .then((res) => {
                                            // response is a custom string from portal webhook. If can both return positive and negative responses.
                                            if(res.data?.includes('Error')){
                                                this.setState({uploading: false, disableButton: false, uploadError: true, uploadErrorMessage: res.data});
                                            } else {
                                                // Return to customer page after save
                                                this.setState({uploading: false, disableButton: false});
                                                this.returnToCustomerPage();
                                            }
                                        })
                                        .catch(err => {
                                            console.log('(POST) Error while trying to create work order:\n', err);
                                            this.setState({uploading: false, disableButton: false, uploadError: true, uploadErrorMessage: JSON.stringify(err)})
                                        })
                                }, 1000);
                            }
                        }
                    >
                        <Form>
                            <div className="d-flex justify-content-between text-white">
                                <h3>Schedule Survey on {this.state.sessionTime} {this.state.timeWindow}</h3>

                                <span className="mx-2 cursor-pointer">
                                    <FontAwesomeIcon icon={faTimes} onClick={() => this.setState({isModelOpen: false, isSchedulingSurvey: false})} />
                                </span>
                            </div>
                            <div className="mt-3 text-white">
                                <label>How Many Stories is the House?*</label>
                                <Field className="select" as="select" name="houseStories">
                                    <option value=""></option>
                                    <option value="NA">NA</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                </Field>
                                <ErrorMessage name="houseStories" component={ErrorLabel} />
                            </div>
                            <div className="mt-3 text-white">
                                <label>CU Preferences/Access Info</label>
                                <Field className="input" type="text" name="codesGatesAnimals" />
                            </div>
                            <div className="mt-3 text-white">
                                <label>Will the Customer be Home?*</label>
                                <Field className="select" as="select" name="customerHome">
                                    <option value=""></option>
                                    <option value="No">No</option>
                                    <option value="Yes">Yes</option>
                                </Field>
                                <ErrorMessage name="customerHome" component={ErrorLabel} />
                            </div>
                            <div className="mt-3 text-white">
                                <label>Do we have permission to access the roof if the customer is not home?*<br></br>(We will not attempt to go inside any structure without the customer on site)</label>
                                <Field className="select" as="select" name="roofAccess">
                                    <option value=""></option>
                                    <option value="No">No</option>
                                    <option value="Yes">Yes</option>
                                </Field>
                                <ErrorMessage name="roofAccess" component={ErrorLabel} />
                            </div>
                            <div className="mt-3 text-white">
                                <label>Do we have permission to access the backyard if the customer is not home?*<br></br>(We will not attempt to go inside any structure without the customer on site)</label>
                                <Field className="select" as="select" name="backyardAccess">
                                    <option value=""></option>
                                    <option value="No">No</option>
                                    <option value="Yes">Yes</option>
                                </Field>
                                <ErrorMessage name="backyardAccess" component={ErrorLabel} />
                            </div>
                            <div className="mt-3 text-white">
                                <label>Survey Type (Select All That Apply)*</label>
                                <Field className="select text-white" as="select" name="surveyType" multiple={true}>
                                    <option className='text-white' value="House">House</option>
                                    <option className='text-white'  value="Additional Structure">Additional Structure</option>
                                    <option className='text-white'  value="Ground Mount">Ground Mount</option>
                                    <option className='text-white'  value="Battery">Battery</option>
                                </Field>
                                <ErrorMessage name="surveyType" component={ErrorLabel} />
                            </div>
                            <div className="mt-3 text-white">
                                <label>Any Additional Survey Notes?</label>
                                <Field className="input" type="text" maxLength="1500" name="addSurveyNotes" />
                            </div>
                            <div className={this.resurvey ? 'd-none' : 'mt-3 text-white'}>
                                <label>Is the Customer Requesting an Attic Run?*</label>
                                <Field className="select" as="select" name="atticRun">
                                    <option value=""></option>
                                    <option value="No">No</option>
                                    <option value="Yes">Yes</option>
                                </Field>
                                <ErrorMessage name="atticRun" component={ErrorLabel} />
                            </div>
                            <div className="mt-3 text-white">
                                <label>Is this a Manufactured Home?*</label>
                                <Field className="select" as="select" name="manuHome">
                                    <option value=""></option>
                                    <option value="No">No</option>
                                    <option value="Yes">Yes</option>
                                </Field>
                                <ErrorMessage name="manuHome" component={ErrorLabel} />
                            </div>
                            <div className="mt-3 text-white">
                                <label>Is there Existing Solar?*</label>
                                <Field className="select" as="select" name="existingSolar">
                                    <option value=""></option>
                                    <option value="No">No</option>
                                    <option value="Yes">Yes</option>
                                </Field>
                                <ErrorMessage name="existingSolar" component={ErrorLabel} />
                            </div>
                            <div className="mt-3 text-white">
                                <label>Is there Internet Access?*</label>
                                <Field className="select" as="select" name="internet">
                                    <option value=""></option>
                                    <option value="None">None</option>
                                    <option value="Wifi & Cell">Wifi & Cell</option>
                                    <option value="Wifi Only">Wifi Only</option>
                                    <option value="Cell Only">Cell Only</option>
                                </Field>
                                <ErrorMessage name="internet" component={ErrorLabel} />
                            </div>
                            {/* Using FormikConsumer to check values that are set in the form. */}
                            <FormikConsumer>
                                {/* Only show wifi network and password fields if the wifi information is not already set on the opportunity */}
                                {({ values }) => (!this.state.opp?.Wifi_Network__c && (values.internet === 'Wifi & Cell' || values.internet === 'Wifi Only') ? (
                                    <React.Fragment>
                                        <div className="mt-3 text-white">
                                            <label>Wifi Network</label>
                                            <Field className="input" type="text" name="wifiNetwork" maxLength="170" />
                                            <ErrorMessage name="wifiNetwork" component={ErrorLabel} />
                                        </div>
                                        <div className="mt-3 text-white">
                                            <label>Wifi Password</label>
                                            <div className="d-flex align-items-center">
                                                <Field className="input" type={this.state.wifiPasswordVisible ? "text" : "password"} name="wifiPassword" maxLength="170" />
                                                <FontAwesomeIcon
                                                    icon={this.state.wifiPasswordVisible ? faEyeSlash : faEye} 
                                                    onClick={() => this.setState({ wifiPasswordVisible: !this.state.wifiPasswordVisible })} 
                                                />
                                            </div>
                                            <ErrorMessage name="wifiPassword" component={ErrorLabel} />
                                        </div>
                                    </React.Fragment>
                                ) : null)}
                            </FormikConsumer>
                            <div className={this.resurvey ? 'd-none' : 'mt-3 text-white'}>
                                <label>Is this a Re-Survey (RSS)?*</label>
                                <Field className="select" as="select" name="isThisRss">
                                    <option value=""></option>
                                    <option value="false">No</option>
                                    <option value="true">Yes</option>
                                </Field>
                                <ErrorMessage name="isThisRss" component={ErrorLabel} />
                            </div>
                            <div className={this.resurvey ? 'd-none' : 'mt-3 text-white'}>
                                <label>What is the Purpose of the Battery?</label>
                                <Field className="select" as="select" name="batteryPurpose">
                                    <option value=""></option>
                                    <option value="Backup Power">Backup Power</option>
                                    <option value="TOU">TOU</option>
                                </Field>
                                <ErrorMessage name="batteryPurpose" component={ErrorLabel} />
                            </div>
                            <div className={this.resurvey ? 'd-none' : 'mt-3 text-white'}>
                                <label>What does the Customer want Backed Up?</label>
                                <Field className="select" as="select" name="batteryBackup">
                                    <option value=""></option>
                                    <option value="Partial">Partial</option>
                                    <option value="Full no AC">Full no AC</option>
                                    <option value="Full with AC">Full with AC</option>
                                </Field>
                                <ErrorMessage name="batteryBackup" component={ErrorLabel} />
                            </div>
                            <div className="mt-3 text-white">
                                <label>RSS Reason(s) (Select All That Apply)</label>
                                <Field className="select text-white" as="select" name="rssNeeds" multiple={true}>
                                    <option value="" hidden disabled></option>
                                    { this.state.rssReasons.map((option) => <option className='text-white' key={option.value} value={option.value}>{option.label}</option>) }
                                </Field>
                            </div>
                            <div className="mt-3">
                                <button className="btn btn-primary" type="submit" disabled={this.state.disableButton}>Schedule Survey</button>
                                <ErrorMessage name="missingField" component={ErrorLabel} />
                            </div>
                            <div className="mt-3">
                                <button className="btn btn-primary" onClick={() => this.setState({isModelOpen: false})}>Cancel</button>
                            </div>
                            <div className={this.state.uploading ? css.fc__Uploading : 'd-none'}></div>
                            <div className={this.state.uploadError ? css.fc__ErrorUpload  : 'd-none'}>{this.state.uploadErrorMessage}</div>
                            <h2>&nbsp;</h2>
                        </Form>
                    </Formik>
                </Modal>
            </div>
            </React.Fragment>
        );
    }
}

// ---------- Redux mapping: ----------
const mapStateToProps = state => {
    return {
        user: state.user
    };
};

export default connect(mapStateToProps)(SurveyCalendar);